import dayjs from 'dayjs';

import { dateInputFormat, frequencies, PCGLQueryKeys } from './constants';
import { CustomFieldType, DataPlaneMerchant, TextFieldValidation } from '../@types/types';
import { LineItem } from '../components/lineItems/line-items';
import { OptionType } from '../components/select/Select';

export const getFrequencyOptions = () => Object.entries(frequencies).map(([value, label]) => ({ value, label }));

type OptionWithOrder = {
	order: number;
} & OptionType;

export const getFrequencyOptionsByName = (freqs: string[]): OptionType[] =>
	freqs.reduce((acc, freq) => {
		const found = frequencies[freq];
		if (found) {
			acc.push({ value: found.name, label: found.text, order: found.order });
		}
		return acc;
	}, [] as OptionWithOrder[]);

export function stringToEnum<T extends Record<string, string | any>>(str: string, enumType: T): T[keyof T] | undefined {
	const matchedKey = Object.keys(enumType).find(key => enumType[key] === str) as keyof T;
	if (matchedKey) {
		return enumType[matchedKey];
	}
	return undefined;
}

export const getNextFirstOrFifteenth = (day: string) => {
	const today = dayjs(day);
	const currentDay = today.date();
	if ([1, 15].includes(currentDay)) {
		return dayjs(today).format(dateInputFormat);
	}
	if (currentDay < 15) {
		return dayjs(today.date(15)).format(dateInputFormat);
	}
	return today.add(1, 'month').date(1).format(dateInputFormat);
};

export const isLettersOnlyInput = (type: string) =>
	TextFieldValidation[type as any as keyof typeof TextFieldValidation] === TextFieldValidation.LettersOnly;

export const customFieldStringToEnum = (type: string) => stringToEnum<typeof CustomFieldType>(type, CustomFieldType);
export const isCustomTextField = customFieldStringToEnum(`Text`);
export const isCustomDropDownField = customFieldStringToEnum(`DropDown`);
export const isCustomNumberField = customFieldStringToEnum(`Number`);
export const isDigit = (key: string) => /^[0-9]$/.test(key);
export const isModifierKey = (str: string) => str.length > 1;

export const getFundName = (keyOrName: string, funds: DataPlaneMerchant['Funds']) => {
	const fund = funds.find(fnd => fnd.FundKey === keyOrName || fnd.FundName === keyOrName);
	if (fund) {
		return fund.FundName;
	}
	return null;
};

export const getErrorMessage = (error: unknown) =>
	typeof error === 'object' && error !== null && 'message' in error
		? (error as { message?: string }).message
		: String(error);

export const formatNumberForDisplay = (number: string | null, precision = 2): string => {
	const parsedNumber = parseFloat(number ?? '');
	if (number === null || Number.isNaN(parsedNumber)) {
		return '';
	}

	return new Intl.NumberFormat('en-US', {
		minimumFractionDigits: precision,
		maximumFractionDigits: precision,
		useGrouping: true,
	}).format(parsedNumber);
};

export const generateMultiFundParams = (index: number) => ({
	amount: `${PCGLQueryKeys.MultiFunds}${index}a`,
	fund: `${PCGLQueryKeys.MultiFunds}${index}f`,
});

export function getLineItemsArray(value: unknown): Array<LineItem> {
	if (!Array.isArray(value)) {
		return [];
	}
	return value.filter(
		(item): item is LineItem =>
			item &&
			typeof item === 'object' &&
			typeof (item as LineItem).Amount === 'string' &&
			(item as LineItem).Amount.trim() !== '' &&
			!Number.isNaN(parseFloat(item.Amount)) &&
			typeof (item as LineItem).FundKeyOrName === 'string' &&
			(item as LineItem).FundKeyOrName.trim() !== ''
	);
}
